import { db } from '@root/firebaseDatabase'
import { showDanger } from '@utils/AlertService'
import { log } from '@reusables/logger'
import store from '../store'
import {
	collection,
	doc,
	query,
	where,
	orderBy,
	limit,
	startAfter,
	getDocs,
	getDoc,
	setDoc,
	updateDoc,
	serverTimestamp,
	addDoc,
	deleteDoc,
	onSnapshot,
	deleteField,
	increment,
	arrayUnion,
	arrayRemove,
	getCountFromServer,
	writeBatch
} from 'firebase/firestore'
import { trackDataOperation, initKnqdTracker } from './KnqdTracker'

/**
 * Universal function to read/listen to documents or collections
 * @param {Object} params
 * @param {string|FirebaseReference} params.ref - Path to document/collection
 * @param {Object} [params.filter] - Query filters
 * @param {number} [params.filter.limit] - Number of documents to fetch
 * @param {Object} [params.filter.where] - Where conditions
 * @param {Object} [params.filter.orderBy] - Order by conditions
 * @param {Object} [params.filter.startAfter] - Pagination cursor
 * @param {boolean} [params.listen] - Whether to set up real-time listener
 * @param {Function} [params.onUpdate] - Callback for real-time updates
 * @param {boolean} [params.count] - Whether to fetch document count
 * @returns {Promise<Object|Array>|Unsubscribe}
 */
export const readData = async ({ ref, filter, listen, onUpdate, count = false }) => {
	// Handle if ref is already a Firebase reference
	const pathRef =
		typeof ref === 'string'
			? ref.split('/').filter(Boolean).length % 2 === 1
				? collection(db, ref)
				: doc(db, ref)
			: ref

	// Determine if path is collection (odd segments) or document (even segments)
	const segments =
		typeof ref === 'string'
			? ref.split('/').filter(Boolean)
			: pathRef.path.split('/').filter(Boolean)

	const isCollection = segments.length % 2 === 1

	// Build query if collection
	let queryRef = pathRef
	if (isCollection && filter) {
		const constraints = []

		// Where clauses
		if (filter.where) {
			Object.entries(filter.where).forEach(([field, condition]) => {
				constraints.push(where(field, condition.operator, condition.value))
			})
		}

		// OrderBy
		if (filter.orderBy) {
			Object.entries(filter.orderBy).forEach(([field, direction]) => {
				constraints.push(orderBy(field, direction || 'desc'))
			})
		}

		// Pagination - Updated to handle document snapshots correctly
		if (filter.startAfter) {
			// Check if we're getting a raw document snapshot or our formatted version
			const cursorDoc = filter.startAfter._delegate || filter.startAfter
			constraints.push(startAfter(cursorDoc))
		}

		// Limit
		if (filter.limit) {
			constraints.push(limit(filter.limit))
		}

		queryRef = query(pathRef, ...constraints)
	}

	// Handle count request
	if (count) {
		try {
			const snapshot = await getCountFromServer(queryRef)
			const resultCount = snapshot.data().count

			// Track count operation
			trackDataOperation('read_count', {
				path: typeof ref === 'string' ? ref : pathRef.path,
				count: resultCount,
				hasFilters: !!filter
			})

			return resultCount
		} catch (error) {
			log('Error fetching document count:', error)
			showDanger(error.message)

			// Track error
			trackDataOperation('read_error', {
				path: typeof ref === 'string' ? ref : pathRef.path,
				operation: 'count',
				error: error.message
			})

			throw error
		}
	}

	// Handle real-time listeners
	if (listen) {
		if (typeof onUpdate !== 'function') {
			throw new Error('onUpdate callback is required for listen')
		}

		// Track listener setup
		trackDataOperation('listen_start', {
			path: typeof ref === 'string' ? ref : pathRef.path,
			isCollection,
			hasFilters: !!filter
		})

		return onSnapshot(
			queryRef,
			(snapshot) => {
				if (isCollection) {
					const docs = snapshot.docs.map((doc) => ({
						id: doc.id,
						ref: doc.ref,
						data: doc.data()
					}))

					// Track listener update
					trackDataOperation('listen_update', {
						path: typeof ref === 'string' ? ref : pathRef.path,
						documentCount: docs.length
					})

					onUpdate(docs)
				} else {
					// Track listener update
					trackDataOperation('listen_update', {
						path: typeof ref === 'string' ? ref : pathRef.path,
						documentCount: 1
					})

					onUpdate({
						id: snapshot.id,
						ref: snapshot.ref,
						data: snapshot.data()
					})
				}
			},
			(error) => {
				log('Error listening to data:', error)
				showDanger(error.message)

				// Track listener error
				trackDataOperation('listen_error', {
					path: typeof ref === 'string' ? ref : pathRef.path,
					error: error.message
				})
			}
		)
	}

	// Handle one-time fetch - Updated to return raw snapshot for pagination
	return new Promise((resolve, reject) => {
		const fetchData = isCollection ? getDocs : getDoc
		const startTime = Date.now()

		fetchData(queryRef)
			.then((response) => {
				const duration = Date.now() - startTime

				if (isCollection) {
					const docs = response.docs.map((doc) => ({
						id: doc.id,
						ref: doc.ref,
						data: doc.data(),
						// Store the raw document snapshot for pagination
						_delegate: doc
					}))

					// Track successful read
					trackDataOperation('read', {
						path: typeof ref === 'string' ? ref : pathRef.path,
						documentCount: docs.length,
						hasFilters: !!filter,
						hasPagination: !!filter?.startAfter,
						duration
					})

					resolve(docs)
				} else {
					// Track successful read
					trackDataOperation('read', {
						path: typeof ref === 'string' ? ref : pathRef.path,
						documentCount: 1,
						duration
					})

					resolve({
						id: response.id,
						ref: response.ref,
						data: response.data(),
						_delegate: response
					})
				}
			})
			.catch((error) => {
				// Track read error
				trackDataOperation('read_error', {
					path: typeof ref === 'string' ? ref : pathRef.path,
					operation: 'fetch',
					error: error.message,
					duration: Date.now() - startTime
				})

				reject(error)
			})
	})
}

/**
 * Recursively finds undefined values in data and logs them for debugging
 * Does NOT modify the data - just reports what would cause Firebase errors
 * @param {Object} data - Data object to check
 * @param {string} [parentPath=''] - Parent path for nested fields
 * @param {number} [depth=0] - Current recursion depth
 * @param {number} [maxDepth=20] - Maximum recursion depth to prevent stack overflow
 * @param {WeakSet} [visited=null] - Track visited objects to prevent circular reference loops
 * @returns {string[]} - Array of field paths with undefined values
 */
const findUndefinedFields = (data, parentPath = '', depth = 0, maxDepth = 20, visited = null) => {
	const undefinedFields = []

	// Prevent stack overflow on deeply nested or circular structures
	if (depth > maxDepth || !data || typeof data !== 'object') {
		return undefinedFields
	}

	// Initialize visited set on first call
	if (!visited) {
		visited = new WeakSet()
	}

	// Skip if we've already visited this object (circular reference)
	if (visited.has(data)) {
		return undefinedFields
	}
	visited.add(data)

	if (Array.isArray(data)) {
		data.forEach((item, index) => {
			const fieldPath = parentPath ? `${parentPath}[${index}]` : `[${index}]`
			if (item === undefined) {
				undefinedFields.push(fieldPath)
			} else if (item !== null && typeof item === 'object') {
				undefinedFields.push(...findUndefinedFields(item, fieldPath, depth + 1, maxDepth, visited))
			}
		})
		return undefinedFields
	}

	Object.entries(data).forEach(([key, value]) => {
		const fieldPath = parentPath ? `${parentPath}.${key}` : key

		if (value === undefined) {
			undefinedFields.push(fieldPath)
		} else if (value !== null && typeof value === 'object') {
			// Skip Firestore sentinel values (serverTimestamp, deleteField, etc.)
			if (value._methodName) return
			// Skip Firestore references (DocumentReference, CollectionReference) - always circular
			if (value.firestore || value._key || value.type === 'document' || value.type === 'collection') return

			undefinedFields.push(...findUndefinedFields(value, fieldPath, depth + 1, maxDepth, visited))
		}
	})

	return undefinedFields
}

/**
 * Checks data for undefined values before Firebase write and throws descriptive error
 * This runs BEFORE Firebase to give better error messages with field paths
 * 
 * Why we fail on undefined:
 * - On 'update': undefined fields are ignored, old values persist (likely not intended)
 * - On 'set' with merge: same issue - old values stay
 * - If you want to DELETE a field, use deleteField() explicitly
 * - If you want to clear a value, use null explicitly
 * 
 * @param {Object} data - Data to validate
 * @param {string} refPath - Document path for error context
 * @throws {Error} - User-friendly error (technical details logged to console)
 */
const validateBeforeWrite = (data, refPath) => {
	const undefinedFields = findUndefinedFields(data)
	if (undefinedFields.length > 0) {
		// Log technical details for developers
		console.error(
			`[DataService] Cannot write to Firestore - undefined values at: ${undefinedFields.join(', ')}`,
			`\nDocument: ${refPath}`,
			`\nFix: Use deleteField() to remove fields or null to clear values.`
		)

		// Track validation error to KnqdTracker
		trackDataOperation('write_error', {
			path: refPath,
			operation: 'validation',
			error: `Undefined values at: ${undefinedFields.join(', ')}`,
			undefinedFields
		})

		// User-friendly error message
		const error = new Error('Unable to save. Please try again or contact support if the problem persists.')
		error.undefinedFields = undefinedFields
		error.documentPath = refPath
		throw error
	}
}

/**
 * Universal function to write/update/delete documents
 * @param {Object} params
 * @param {string|FirebaseReference} params.ref - Path to document
 * @param {string} params.operation - 'add'|'set'|'update'|'delete'
 * @param {Object} [params.data] - Data to write (not needed for delete)
 * @param {boolean} [params.merge] - Merge option for set operation
 * @param {boolean} [params.danger] - Show danger alert
 * @returns {Promise<DocumentReference|boolean>}
 */
export const writeData = ({ ref, operation, data, merge = false, danger = true }) => {
	if (!ref) {
		throw new Error('Reference (ref) is required')
	}
	if (!operation || !['add', 'set', 'update', 'delete'].includes(operation)) {
		throw new Error('Valid operation is required: add, set, update, delete')
	}
	if (!data && operation !== 'delete') {
		throw new Error('Data is required for add, set, and update operations')
	}
	// Handle if ref is already a Firebase reference
	const docRef =
		typeof ref === 'string'
			? dbRef(ref) // Convert string path to reference
			: ref // Use existing reference
	const segments =
		typeof ref === 'string'
			? ref.split('/').filter(Boolean)
			: docRef.path.split('/').filter(Boolean)
	const isCollection = segments.length % 2 === 1
	const currentUser = store.getters.getCurrentUser?.uid

	// Create user reference if currentUser is available
	const userRef = currentUser
		? dbRef(`tenant/${store.getters.getTenantId}/users/${currentUser}`)
		: null

	// Get reference path for logging
	const refPath = typeof ref === 'string' ? ref : docRef.path

	// Validate data before write - throws descriptive error if undefined values found
	if (data && operation !== 'delete') {
		validateBeforeWrite(data, refPath)
	}

	// Handle adding new document to collection
	if (operation === 'add' && isCollection) {
		const { createdAt: _ca, createdBy: _cb, updatedAt: _ua, updatedBy: _ub, ...cleanData } = data
		const startTime = Date.now()

		return addDoc(docRef, {
			...cleanData,
			createdAt: serverTimestamp(),
			updatedAt: serverTimestamp(),
			createdBy: userRef,
			updatedBy: userRef
		})
			.then((result) => {
				// Track successful add
				trackDataOperation('write_add', {
					path: refPath,
					documentId: result.id,
					fieldCount: Object.keys(cleanData).length,
					duration: Date.now() - startTime
				})
				return result
			})
			.catch((error) => {
				// Track error
				trackDataOperation('write_error', {
					path: refPath,
					operation: 'add',
					error: error.message,
					duration: Date.now() - startTime
				})
				throw error
			})
	}

	// Handle document operations
	if (isCollection) {
		throw new Error('Invalid document reference')
	}

	const operations = {
		set: () => {
			const { createdAt: _ca, createdBy: _cb, updatedAt: _ua, updatedBy: _ub, ...cleanData } = data
			return setDoc(
				docRef,
				{
					...cleanData,
					updatedAt: serverTimestamp(),
					updatedBy: userRef
				},
				{ merge }
			)
		},
		update: () => {
			const { createdAt: _ca, createdBy: _cb, updatedAt: _ua, updatedBy: _ub, ...cleanData } = data
			return updateDoc(docRef, {
				...cleanData,
				updatedAt: serverTimestamp(),
				updatedBy: userRef
			})
		},
		delete: () => deleteDoc(docRef)
	}

	if (!operations[operation]) {
		throw new Error(`Invalid operation: ${operation}`)
	}

	const startTime = Date.now()
	return operations[operation]()
		.then(() => {
			log(`${operation} operation successful on ${docRef.path}`)

			// Track successful write operation
			const trackingData = {
				path: refPath,
				operation,
				duration: Date.now() - startTime
			}

			// Add field count for set/update operations
			if (operation !== 'delete' && data) {
				trackingData.fieldCount = Object.keys(data).length
			}

			// Add merge flag for set operation
			if (operation === 'set') {
				trackingData.merge = merge
			}

			trackDataOperation(`write_${operation}`, trackingData)

			return true
		})
		.catch((error) => {
			// Track write error
			trackDataOperation('write_error', {
				path: refPath,
				operation,
				error: error.message,
				duration: Date.now() - startTime
			})

			if (danger) {
				log(`Error in ${operation} operation:`, error)
				showDanger(error.message)
			}
			throw error
		})
}

/**
 * Creates a Firestore reference based on path segments
 * @param {string} path - Path to document/collection
 * @param {object} [database=db] - Firestore database instance
 * @returns {DocumentReference|CollectionReference}
 */
export const dbRef = (path) => {
	const segments = path.split('/').filter(Boolean)
	const isCollection = segments.length % 2 === 1
	return isCollection ? collection(db, path) : doc(db, path)
}

/**
 * Get path and collection to check if path exists
 * @async
 * @param path
 * @param collection
 * @param tenantId
 * @returns {Promise<boolean>}
 */
export const getDuplicatedPath = async (path, collection, tenantId, type) => {
	const where = {
		path: {
			operator: '==',
			value: path
		}
	}

	let duplicatedPath = false
	if (type === 'update') {
		await readData({
			ref: `/tenant/${tenantId}/${collection}/`,
			filter: { where }
		})
			.then((results) => {
				if (results.length === 1 && results[0].id !== path.id) {
					duplicatedPath = true
					showDanger('The path already exists')
				}
			})
			.catch((error) => {
				log('~ file: DataService.js ~ line 253 ~ getDuplicatedPath ~ error', error)
				showDanger(error.message)
			})

		return duplicatedPath
	}

	await readData({
		ref: `/tenant/${tenantId}/${collection}/`,
		filter: { where }
	})
		.then((results) => {
			if (results.length === 1) {
				duplicatedPath = true
				showDanger('The path already exists')
			}
		})
		.catch((error) => {
			log('~ file: DataService.js ~ line 268 ~ getDuplicatedPath ~ error', error)
			showDanger(error.message)
		})

	await readData({
		ref: `/tenant/${tenantId}/utilities/redirects/`
	})
		.then((response) => {
			const redirects = response.data?.courses
			if (redirects) {
				if (Object.keys(redirects).includes(path)) {
					duplicatedPath = true
					showDanger('The path already exists')
				}
			}
		})
		.catch((error) => {
			log('~ file: DataService.js ~ line 290 ~ await readData ~ error', error)
			showDanger(error.message)
		})

	return duplicatedPath
}

/**
 * Universal function to perform batch writes with multiple operation types
 * @param {Array} operations - Array of operations to perform in the batch
 * @param {string} operations[].ref - Path to document/collection
 * @param {string} operations[].operation - 'add'|'set'|'update'|'delete'
 * @param {Object} [operations[].data] - Data to write (not needed for delete)
 * @param {boolean} [operations[].merge] - Merge option for set operation
 * @returns {Promise<void>}
 */
export const batchWrite = async (operations) => {
	const batch = writeBatch(db)
	const startTime = Date.now()
	const currentUser = store.getters.getCurrentUser?.uid
	const tenantId = store.getters.getTenantId
	const userRef = currentUser ? dbRef(`tenant/${tenantId}/users/${currentUser}`) : null

	operations.forEach(({ ref, operation = 'update', data, merge = false }) => {
		const refPath = typeof ref === 'string' ? ref : ref.path
		const segments = refPath.split('/').filter(Boolean)
		const isCollection = segments.length % 2 === 1

		switch (operation) {
			case 'add': {
				// For collections, create a new doc reference
				const collectionRef = isCollection ? dbRef(refPath) : null
				if (!collectionRef) {
					throw new Error('add operation requires a collection path')
				}
				const newDocRef = doc(collectionRef)
				batch.set(newDocRef, {
					...data,
					createdAt: serverTimestamp(),
					updatedAt: serverTimestamp(),
					createdBy: userRef,
					updatedBy: userRef
				})
				break
			}
			case 'set': {
				const docRef = dbRef(refPath)
				const { createdAt: _ca, createdBy: _cb, updatedAt: _ua, updatedBy: _ub, ...cleanData } = data
				batch.set(
					docRef,
					{
						...cleanData,
						updatedAt: serverTimestamp(),
						updatedBy: userRef
					},
					{ merge }
				)
				break
			}
			case 'update': {
				const docRef = dbRef(refPath)
				const { createdAt: _ca, createdBy: _cb, updatedAt: _ua, updatedBy: _ub, ...cleanData } = data
				batch.update(docRef, {
					...cleanData,
					updatedAt: serverTimestamp(),
					updatedBy: userRef
				})
				break
			}
			case 'delete': {
				const docRef = dbRef(refPath)
				batch.delete(docRef)
				break
			}
			default:
				throw new Error(`Invalid batch operation: ${operation}`)
		}
	})

	try {
		await batch.commit()
		log('Batch write successful')

		// Track successful batch operation
		trackDataOperation('batch_write', {
			operationCount: operations.length,
			paths: operations.map((op) => (typeof op.ref === 'string' ? op.ref : op.ref.path)),
			duration: Date.now() - startTime
		})
	} catch (error) {
		log('Error in batch write:', error)
		showDanger('Batch write failed')

		// Track batch error
		trackDataOperation('batch_error', {
			operationCount: operations.length,
			error: error.message,
			duration: Date.now() - startTime
		})

		throw error
	}
}

// Export the firestore field functions
export { serverTimestamp, deleteField, increment, arrayUnion, arrayRemove }

// Export knqd.to tracker initialization
export { initKnqdTracker }
